/**
 * Go Fish bot.
 *
 * - Asks for the rank it holds most of (closest to a book), preferring a rank that another player was recently
 *   heard asking for, because that player must be holding it. How much of the public ask log it uses depends on
 *   the difficulty's memory.
 * - With no lead on a rank it asks the player holding the most cards.
 * The bot only ever reads its own seat's view.
 */
import { chance, type BotContext, type BotEngine } from '../../bots/types.js';
import { goFishCounts, goFishLegal, type GoFishAction, type GoFishLegal, type GoFishView } from './engine.js';

/** Seats that are known from the log to hold `rank`: they asked for it and have not handed it over since. */
function knownHolders(view: GoFishView, rank: number): number[] {
  const holders = new Set<number>();
  for (const a of view.log) {
    if (a.rank !== rank) continue;
    holders.add(a.seat);
    if (a.got > 0) holders.delete(a.target);
  }
  holders.delete(view.seat ?? -1);
  return [...holders].filter((s) => view.handCounts[s]! > 0);
}

function choose(view: GoFishView, legal: GoFishLegal, ctx: BotContext): GoFishAction {
  const counts = goFishCounts(view.hand);
  const remember = ctx.profile.memory > 0 && chance(ctx, ctx.profile.memory);
  let best: { rank: number; target: number; score: number } | null = null;
  for (const rank of legal.ranks) {
    const holders = remember ? knownHolders(view, rank).filter((s) => legal.targets.includes(s)) : [];
    const target = holders[0] ?? [...legal.targets].sort((a, b) => view.handCounts[b]! - view.handCounts[a]!)[0]!;
    const score = (counts.get(rank) ?? 0) * 10 + (holders.length > 0 ? 25 : 0);
    if (!best || score > best.score) best = { rank, target, score };
  }
  return { type: 'ask', target: best!.target, rank: best!.rank };
}

export const goFishBot: BotEngine<GoFishView, GoFishLegal, GoFishAction> = {
  decideAction(view, legal, ctx) {
    if (chance(ctx, ctx.profile.mistakeRate)) {
      return { type: 'ask', target: legal.targets[ctx.rng.int(legal.targets.length)]!, rank: legal.ranks[ctx.rng.int(legal.ranks.length)]! };
    }
    return choose(view, legal, ctx);
  },

  evaluateState(view) {
    if (view.seat === null) return 0;
    const mine = view.totals[view.seat]!;
    const best = Math.max(...view.totals.filter((_, s) => s !== view.seat));
    return Math.max(0, Math.min(1, 0.5 + (mine - best) / 13));
  },

  calculateRisk() {
    return 0;
  },

  handleTimeout(view, legal) {
    const now = legal.ranks.length > 0 ? legal : goFishLegal(view.hand, view.handCounts, view.seat ?? 0);
    const counts = goFishCounts(view.hand);
    const rank = [...now.ranks].sort((a, b) => (counts.get(b) ?? 0) - (counts.get(a) ?? 0) || b - a)[0]!;
    return { type: 'ask', target: now.targets[0]!, rank };
  },
};
